import React from 'react';
import { IconButton } from '@chakra-ui/react';
import { FiGlobe } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import type { SupportedLanguage } from '../i18n';

interface LanguageSwitcherProps {
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

/**
 * Toggles the UI language between English and Arabic. Persists the choice in
 * localStorage (via i18next) and, when authenticated, to the backend so the
 * preference follows the user across devices.
 */
const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ size = 'sm' }) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();

  const current = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const nextLang: SupportedLanguage = current === 'ar' ? 'en' : 'ar';

  const handleToggle = async () => {
    await i18n.changeLanguage(nextLang);
    if (isAuthenticated) {
      try {
        await authAPI.updatePreferredLanguage(nextLang);
      } catch (err) {
        console.error('Failed to persist language preference:', err);
      }
    }
  };

  return (
    <IconButton
      aria-label={t('language.switch')}
      title={nextLang === 'ar' ? t('language.arabic') : t('language.english')}
      variant="ghost"
      size={size}
      onClick={handleToggle}
    >
      <FiGlobe />
    </IconButton>
  );
};

export default LanguageSwitcher;
